import type { ReactNode } from 'react';
import { FormSection, Spacer } from '@ksyos/design-system';
import { useEffect, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import type { ProcessActivityMedicationData } from '@common/validation/customControls/processActivityMedicationData';
import type { Medications } from '@frontend/components/pages/customControls/utils/v1/medications/medications';
import type { ValidationErrors } from '@frontend/utils/validation';
import { MedicationsSelectActions } from '@common/enums';
import { formatString } from '@common/utils/formatString';
import { never } from '@common/utils/never';
import { LinkOnClick } from '@frontend/components/elements/common';
import {
    filterAndPrepareMedicationsForSaving,
    isCurrentMedication,
    loadProcessActivityMedications,
    MedicationRecord,
} from '@frontend/components/pages/customControls/utils/v1/medications/medications';
import { MedicationsAddEditTable } from '@frontend/components/pages/customControls/utils/v1/medications/MedicationsAddEditTable';
import { MedicationsCollapsible } from '@frontend/components/pages/customControls/utils/v1/medications/MedicationsCollapsible';
import { MedicationsPickableTable } from '@frontend/components/pages/customControls/utils/v1/medications/MedicationsPickableTable';
import { MedicationsPickableTableRow } from '@frontend/components/pages/customControls/utils/v1/medications/MedicationsPickableTableRow';
import { checkMedicationsHaveErrors } from '@frontend/components/pages/customControls/validateMedications';
import { ProfessionalSummaryDataAction } from '@frontend/components/pages/professionalSummaryData/ProfessionalSummaryDataSection';
import { getSelectedLanguage } from '@frontend/translate';
import { usePatientEpisodeCategoryContext } from '@frontend/utils/hooks/usePatientEpisodeCategoryContext';
import { BooleanRadioGroupFormGroup } from '../processActivityFormGroupFields';
import { MedicationsAddEditTableRow } from './components/MedicationsAddEditTableRow';

interface ProcessActivityMedicationsAddEditProps {
    errors?: ValidationErrors;
    onChange: (data: Partial<ProcessActivityMedicationData>, validateKeys?: string | string[] | undefined) => void;
    patientUsesMedication: boolean | null;
    processActivityId?: string;
}

export function ProcessActivityMedicationsAddEdit(props: ProcessActivityMedicationsAddEditProps) {
    const { processActivityId, patientUsesMedication } = props;
    const { patient } = usePatientEpisodeCategoryContext();

    const [checkedHisMedicationsCount, setCheckedHisMedicationsCount] = useState(0);
    const [hasPickableMedications, setHasPickableMedications] = useState(false);
    const [hisTableRows, setHisTableRows] = useState<ReactNode[]>([]);
    const [isHisMedicationsOpen, setIsHisMedicationsOpen] = useState(false);
    const [isManualMedicationsOpen, setIsManualMedicationsOpen] = useState(false);
    const [manualMedicationsCount, setManualMedicationsCount] = useState(0);
    const [manualTableRows, setManualTableRows] = useState<ReactNode[]>([]);
    const [medications, setMedications] = useState<Medications>();
    const [validationKeys, setValidationKeys] = useState<string[]>([]);

    const translate = getSelectedLanguage();

    useEffect(() => {
        loadMedications();
    }, []);

    useEffect(() => {
        setMedicationsRows();
    }, [medications, props.errors]);

    if (!medications) {
        return null;
    }

    return (
        <FormSection title={translate.medications.medication}>
            <BooleanRadioGroupFormGroup
                fieldKey="patientUsesMedication"
                isRequired={true}
                label={translate.medications.patientUsesMedication}
                onChange={async (value) => setPatientUsesMedication(!!value)}
                tooltipText={translate.medications.patientUsesMedicationTooltip}
                type="radio"
            />

            {patientUsesMedication && (
                <>
                    {hasPickableMedications && (
                        <MedicationsCollapsible
                            className={checkMedicationsHaveErrors(props.errors) ? 'has-danger' : ''}
                            extraHeaderElements={
                                <Spacer
                                    direction="horizontal"
                                    spacing="small"
                                >
                                    <LinkOnClick
                                        className="mr-2"
                                        onClick={async () => await selectActions(MedicationsSelectActions.all)}
                                    >
                                        {translate.professionalSummary.all}
                                    </LinkOnClick>
                                    <LinkOnClick
                                        className="mr-2"
                                        onClick={async () => await selectActions(MedicationsSelectActions.current)}
                                    >
                                        {translate.professionalSummary.current}
                                    </LinkOnClick>
                                    <LinkOnClick
                                        className="mr-2"
                                        onClick={async () => await selectActions(MedicationsSelectActions.none)}
                                    >
                                        {translate.professionalSummary.none}
                                    </LinkOnClick>
                                </Spacer>
                            }
                            header={translate.medications.addMedicationsFromHis}
                            isOpen={isHisMedicationsOpen && hasPickableMedications}
                            onToggle={() => setIsHisMedicationsOpen(!isHisMedicationsOpen)}
                            subHeader={
                                checkedHisMedicationsCount
                                    ? formatString(translate.medications.numberOfMedicationsSelected, [
                                          checkedHisMedicationsCount.toString(),
                                      ])
                                    : ''
                            }
                        >
                            <MedicationsPickableTable tableRows={hisTableRows} />
                        </MedicationsCollapsible>
                    )}
                    <MedicationsCollapsible
                        className={checkMedicationsHaveErrors(props.errors) ? 'has-danger' : ''}
                        header={translate.medications.addMedicationsManually}
                        isOpen={isManualMedicationsOpen}
                        onToggle={() => {
                            if (!checkMedicationsHaveErrors(props.errors) || !isManualMedicationsOpen) {
                                handleToggleManualMedications();
                            }
                        }}
                        subHeader={
                            manualMedicationsCount
                                ? formatString(translate.medications.numberOfMedicationsManuallyAdded, [
                                      manualMedicationsCount.toString(),
                                  ])
                                : ''
                        }
                    >
                        <MedicationsAddEditTable
                            addEmptyMedicationRecord={async () => await addEmptyMedicationRecord()}
                            tableRows={manualTableRows}
                        />
                    </MedicationsCollapsible>
                </>
            )}
        </FormSection>
    );

    function setPatientUsesMedication(value: boolean) {
        props.onChange({ patientUsesMedication: value }, 'patientUsesMedication');

        if (!value) {
            handlePatientUsesMedicationChange(value);
        }
    }

    async function handleToggleManualMedications() {
        setIsManualMedicationsOpen(!isManualMedicationsOpen);
        if (
            medications &&
            !medications.some((medication) => medication?.action === ProfessionalSummaryDataAction.add)
        ) {
            await addEmptyMedicationRecord();
        }
    }

    async function loadMedications() {
        const initialMedications = await loadProcessActivityMedications(patient, processActivityId);
        setMedications(initialMedications);

        if (initialMedications && initialMedications.size > 0) {
            setPatientUsesMedication(true);
        }
    }

    function setMedicationsRows() {
        if (!medications) {
            return;
        }

        let arePickableMedicationsAvailable = false;
        let currentHisMedicationsCount = 0;
        let currentManualMedicationsCount = 0;
        const currentHisTableRows = [];
        const currentManualTableRows = [];

        let totalManualMedications = 0;
        for (const [key, medication] of medications.entrySeq().toArray() as [string, MedicationRecord][]) {
            if (medication.action === ProfessionalSummaryDataAction.add) {
                currentManualTableRows.push(
                    <MedicationsAddEditTableRow
                        errors={props.errors}
                        index={currentHisMedicationsCount + totalManualMedications}
                        key={key}
                        medication={medication}
                        medicationKey={key}
                        medications={medications}
                        onChange={async (newMedications, keysToValidate) =>
                            await onChange(newMedications, keysToValidate, true)
                        }
                    />
                );
                totalManualMedications++;
                if (medication.name) {
                    currentManualMedicationsCount++;
                }
            } else {
                arePickableMedicationsAvailable = true;
                currentHisTableRows.push(
                    <MedicationsPickableTableRow
                        key={key}
                        medication={medication}
                        medicationKey={key}
                        medications={medications}
                        onChange={async (newMedications) => await onChange(newMedications, validationKeys, true)}
                    />
                );
                if (medication.action === ProfessionalSummaryDataAction.pick) {
                    currentHisMedicationsCount++;
                }
            }
        }

        setHasPickableMedications(arePickableMedicationsAvailable);
        setManualMedicationsCount(currentManualMedicationsCount);
        setCheckedHisMedicationsCount(currentHisMedicationsCount);
        setHisTableRows(currentHisTableRows);
        setManualTableRows(currentManualTableRows);
    }

    async function addEmptyMedicationRecord() {
        if (medications) {
            await onChange(medications.set(uuidv4(), new MedicationRecord()), validationKeys);
        }
    }

    async function selectActions(action: MedicationsSelectActions) {
        let finalMedication;
        if (medications) {
            switch (action) {
                case MedicationsSelectActions.all:
                    finalMedication = medications.map((medication) => {
                        if (medication && medication.action === ProfessionalSummaryDataAction.skip) {
                            return medication.set('action', ProfessionalSummaryDataAction.pick);
                        } else {
                            return medication;
                        }
                    });
                    break;
                case MedicationsSelectActions.current:
                    finalMedication = medications.map((medication) => {
                        if (medication && medication.action !== ProfessionalSummaryDataAction.add) {
                            return medication.set(
                                'action',
                                isCurrentMedication(medication)
                                    ? ProfessionalSummaryDataAction.pick
                                    : ProfessionalSummaryDataAction.skip
                            );
                        } else {
                            return medication;
                        }
                    });
                    break;
                case MedicationsSelectActions.none:
                    finalMedication = medications.map((medication) => {
                        if (medication && medication.action === ProfessionalSummaryDataAction.pick) {
                            return medication.set('action', ProfessionalSummaryDataAction.skip);
                        } else {
                            return medication;
                        }
                    });

                    break;
                default:
                    never(action);
            }
        }

        await onChange(finalMedication as Medications, validationKeys);
    }

    async function onChange(
        changedMedications: Medications,
        keysToBeValidated: string[],
        skipAddMedications?: boolean
    ) {
        setMedications(changedMedications);

        if (keysToBeValidated.length > 0 && !keysToBeValidated.some((key) => validationKeys.includes(key))) {
            setValidationKeys([...validationKeys, ...keysToBeValidated]);
        }

        props.onChange(
            {
                medications: filterAndPrepareMedicationsForSaving(changedMedications, skipAddMedications),
            },
            validationKeys
        );
    }

    function handlePatientUsesMedicationChange(value: boolean | null) {
        if (medications && value === false) {
            const newMedications = medications.map((medication) => {
                if (medication?.action === ProfessionalSummaryDataAction.pick) {
                    return medication.set('action', ProfessionalSummaryDataAction.skip);
                } else {
                    return medication;
                }
            });
            const finalMedications = (newMedications as Medications)
                .filter((medication) => medication?.action !== ProfessionalSummaryDataAction.add)
                .toOrderedMap();
            onChange(finalMedications, validationKeys);

            setIsManualMedicationsOpen(false);
            setIsHisMedicationsOpen(false);
        }
    }
}
